import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from '@/src/core/utils';

const THEME_STORAGE_KEY = '@tournament_app_theme';

export interface ThemeColors {
  background: string;
  surface: string;
  card: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  primary: string;
  primaryText: string;
  success: string;
  error: string;
  border: string;
  divider: string;
  badgeSetup: string;
  badgeProgress: string;
  badgeCompleted: string;
  overlay: string;
  shadow: string;
}

// Vanilla Cream - Warm and elegant light theme
const lightColors: ThemeColors = {
  background: '#FBF8F3',
  surface: '#FFFFFF',
  card: '#FFFDF9',
  text: '#2C2416',
  textSecondary: '#6B5D4D',
  textTertiary: '#9A8C7B',
  primary: '#C4873B',
  primaryText: '#FFFFFF',
  success: '#5B9A5B',
  error: '#C75450',
  border: '#E8E0D5',
  divider: '#F0EBE3',
  badgeSetup: '#FFF3CD',
  badgeProgress: '#E3EFF8',
  badgeCompleted: '#E0F0E0',
  overlay: 'rgba(44,36,22,0.5)',
  shadow: 'rgba(44,36,22,0.10)',
};

// Midnight Blue - Modern and professional dark theme
const darkColors: ThemeColors = {
  background: '#0A1628',
  surface: '#132238',
  card: '#1A2F4A',
  text: '#E8F1FF',
  textSecondary: '#8BA3C7',
  textTertiary: '#5A7499',
  primary: '#4A9FFF',
  primaryText: '#FFFFFF',
  success: '#2DD4A8',
  error: '#FF6B7A',
  border: '#2A4066',
  divider: '#1E3550',
  badgeSetup: '#3D3520',
  badgeProgress: '#1A3A5C',
  badgeCompleted: '#1A4A3A',
  overlay: 'rgba(10,22,40,0.85)',
  shadow: 'rgba(0,0,0,0.50)',
};

interface ThemeContextType {
  isDark: boolean;
  colors: ThemeColors;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load theme preference on mount
  useEffect(() => {
    loadThemePreference();
  }, []);

  const loadThemePreference = async () => {
    try {
      const savedTheme = await AsyncStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme !== null) {
        setIsDark(savedTheme === 'dark');
        logger.debug('Theme', 'Loaded theme preference', { theme: savedTheme });
      }
    } catch (error) {
      logger.warn('Theme', 'Failed to load theme preference', { error });
    } finally {
      setIsLoading(false);
    }
  };

  const saveThemePreference = async (theme: 'light' | 'dark') => {
    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, theme);
      logger.debug('Theme', 'Saved theme preference', { theme });
    } catch (error) {
      logger.warn('Theme', 'Failed to save theme preference', { error });
    }
  };

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    saveThemePreference(newTheme ? 'dark' : 'light');
  };

  const colors = isDark ? darkColors : lightColors;

  // Show loading indicator while loading preference
  // This prevents flash of wrong theme
  if (isLoading) {
    return null; // or a splash screen
  }

  return (
    <ThemeContext.Provider value={{ isDark, colors, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}